import { SitePage, COMPANY } from './SiteChrome';
import { useI18n } from '@/lib/i18n';
import { Banner } from '@/ui';
import './pages.css';

/**
 * Imprint and privacy notice.
 *
 * The imprint is factual company data (§5 DDG). The privacy text is a working
 * draft that states what the product actually does — it carries a visible notice
 * that it needs legal review before launch.
 */
export default function Legal({ page }: { page: 'imprint' | 'privacy' }) {
  const { t, lang } = useI18n();
  const german = lang === 'de';

  return (
    <SitePage>
      <section className="prose-page">
        <div className="site-shell prose">
          <h1 className="section-title">{page === 'imprint' ? t.site.legal.imprint : t.site.legal.privacy}</h1>
        </div>

        {page === 'imprint' ? (
          <div className="site-shell">
            {/* Company facts as a finding panel, deliberately outside the
             * `.prose` wrapper above — see pages.css for why: `.prose dl`
             * shares `.ins-finding-def`'s specificity and would otherwise
             * silently win and override it. */}
            <div className="ins-finding ins-prose legal-imprint">
              <dl className="ins-finding-def">
                <dt>{COMPANY.name}</dt>
                <dd>
                  {COMPANY.street}, {COMPANY.city}, {COMPANY.country}
                </dd>
                <dt>{t.site.legal.managingDirectors}</dt>
                <dd>{COMPANY.directors}</dd>
                <dt>E-Mail</dt>
                <dd>
                  <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
                </dd>
                <dt>Web</dt>
                <dd>
                  <a href={`https://${COMPANY.web}`} target="_blank" rel="noreferrer noopener">
                    {COMPANY.web}
                  </a>
                </dd>
                <dt>{t.site.legal.vatId}</dt>
                <dd>{COMPANY.vat}</dd>
                <dt>{t.site.legal.responsible}</dt>
                <dd>{COMPANY.directors}</dd>
              </dl>
            </div>
          </div>
        ) : (
          <div className="site-shell prose">
            <>
              <Banner tone="warning">{t.site.legal.draftNotice}</Banner>

              {german ? (
                <>
                  <h2>Verantwortlicher</h2>
                  <p>
                    {COMPANY.name}, {COMPANY.street}, {COMPANY.city}. Fragen zum Datenschutz an{' '}
                    <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
                  </p>

                  <h2>Welche Daten wir verarbeiten</h2>
                  <ul>
                    <li>
                      <strong>Kontoangaben:</strong> Name, dienstliche E-Mail-Adresse, Organisationsname und ein
                      Passwort-Hash. Grundlage ist die Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO).
                    </li>
                    <li>
                      <strong>Bewertungsinhalte:</strong> Kriterien, Gewichte, Noten, Kommentare und die Dokumente, die
                      Sie hochladen. Diese Inhalte gehören Ihrer Organisation.
                    </li>
                    <li>
                      <strong>Anbieterantworten:</strong> Angaben, die eingeladene Anbieter über den privaten Link
                      eintragen, einschließlich Name und E-Mail der Kontaktperson.
                    </li>
                    <li>
                      <strong>Anfragen:</strong> Was Sie in das Kontaktformular schreiben, zur Beantwortung Ihrer
                      Anfrage.
                    </li>
                    <li>
                      <strong>Protokolle:</strong> Änderungen an einer Bewertung mit Person, Aktion und Zeitpunkt.
                    </li>
                  </ul>

                  <h2>KI-Verarbeitung</h2>
                  <p>
                    Sind die KI-Werkzeuge aktiv, wird Bewertungstext — Kriterien, Erwartungen, Auszüge aus
                    Anbieterdokumenten und anonymisierte Noten — zur Auswertung an einen Modellanbieter übermittelt.
                    Namen und E-Mail-Adressen werden dabei nicht übertragen. In einer privaten Instanz bestimmen Sie den
                    Endpunkt selbst.
                  </p>

                  <h2>Speicherdauer</h2>
                  <p>
                    Inhalte bleiben, solange Ihr Arbeitsbereich besteht. Demo-Arbeitsbereiche werden automatisch nach 24
                    Stunden vollständig gelöscht. Auf Wunsch löschen wir Ihren Arbeitsbereich.
                  </p>

                  <h2>Ihre Rechte</h2>
                  <p>
                    Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit und Widerspruch. Eine
                    Beschwerde bei einer Aufsichtsbehörde ist jederzeit möglich. Bewertungen lassen sich jederzeit als
                    Entscheidungspaket und CSV exportieren.
                  </p>

                  <h2>Cookies</h2>
                  <p>
                    Wir setzen ein technisch notwendiges Sitzungs-Cookie. Es gibt kein Tracking und keine Werbe-Cookies.
                    Sprache und Darstellung werden lokal im Browser gespeichert.
                  </p>
                  <p>
                    Wir f&uuml;hren au&szlig;erdem anonyme Z&auml;hlungen der Besuche dieser Website &mdash; wie viele Seiten
                    aufgerufen und welche Links genutzt wurden &mdash;, ohne dass erkennbar ist, wer die Seite besucht hat.
                    Diese Z&auml;hlung verwendet kein Cookie, speichert keine IP-Adresse und ist kein Tracking.
                  </p>
                </>
              ) : (
                <>
                  <h2>Controller</h2>
                  <p>
                    {COMPANY.name}, {COMPANY.street}, {COMPANY.city}, {COMPANY.country}. Privacy questions:{' '}
                    <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
                  </p>

                  <h2>What we process</h2>
                  <ul>
                    <li>
                      <strong>Account data:</strong> name, work email, organisation name and a password hash, to provide
                      the service you signed up for (Art. 6(1)(b) GDPR).
                    </li>
                    <li>
                      <strong>Evaluation content:</strong> criteria, weights, scores, comments and the documents you
                      upload. This content belongs to your organisation.
                    </li>
                    <li>
                      <strong>Vendor responses:</strong> what invited vendors enter through their private link,
                      including the contact name and email you sent it to.
                    </li>
                    <li>
                      <strong>Enquiries:</strong> what you write in the contact form, so we can answer it.
                    </li>
                    <li>
                      <strong>Audit records:</strong> changes to an evaluation with actor, action and timestamp.
                    </li>
                  </ul>

                  <h2>AI processing</h2>
                  <p>
                    When the AI tools are enabled, evaluation text — criteria, expectations, extracts from vendor
                    documents and anonymised scores — is sent to a model provider for analysis. Names and email
                    addresses are not part of that payload. On a private instance you choose the endpoint yourself.
                  </p>

                  <h2>Retention</h2>
                  <p>
                    Content stays for as long as your workspace exists. Demo workspaces are deleted in full after 24
                    hours. We delete your workspace on request.
                  </p>

                  <h2>Your rights</h2>
                  <p>
                    Access, rectification, erasure, restriction, portability and objection, and you may complain to a
                    supervisory authority. Evaluations can be exported at any time as a decision pack and as CSV.
                  </p>

                  <h2>Cookies</h2>
                  <p>
                    One strictly necessary session cookie. No tracking and no advertising cookies. Language and theme
                    are stored locally in your browser.
                  </p>
                  <p>
                    We also keep anonymous counts of visits to this site &mdash; how many pages were viewed and which links
                    were used &mdash; with nothing that identifies who visited. These counts use no cookie, store no IP
                    address, and are not tracking.
                  </p>
                </>
              )}
            </>
          </div>
        )}
      </section>
    </SitePage>
  );
}
